import { Body, Icon } from '@assembly-js/design-system';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import OTPInput from 'react-otp-input';

import { MFA_CODE_LENGTH } from 'src/constants/authConsts';

import { StudioPageFrame } from './StudioPageFrame';

export interface StudioMfaChallengeProps {
  email: string;
  onSubmitCode: (code: string) => Promise<void>;
}

export const StudioMfaChallenge = ({
  email,
  onSubmitCode,
}: StudioMfaChallengeProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (otp: string) => {
    setCode(otp);
    setError(undefined);
    if (otp.length === MFA_CODE_LENGTH) {
      void (async () => {
        setIsSubmitting(true);
        try {
          await onSubmitCode(otp);
        } catch (err) {
          setError(
            err instanceof Error
              ? err.message
              : t(($) => $.page_onboarding.studio.login.mfa_failed),
          );
        } finally {
          setIsSubmitting(false);
        }
      })();
    }
  };

  return (
    <StudioPageFrame>
      <Icon icon="AssemblyLogo" className="mb-3 h-8 w-8 text-black" />
      <Body className="font-studio text-2xl text-black text-center sm:text-[26px]">
        {t(($) => $.page_onboarding.studio.login.mfa_title)}
      </Body>
      <Body
        size="lg"
        className="mb-0 mt-3 font-studio text-center text-[#808080]"
      >
        {t(($) => $.page_onboarding.studio.login.mfa_subtitle, { email })}
      </Body>
      <div className="mt-10 flex w-full justify-center">
        <OTPInput
          value={code}
          onChange={handleChange}
          numInputs={MFA_CODE_LENGTH}
          inputType="tel"
          shouldAutoFocus
          renderInput={(inputProps) => (
            <input
              {...inputProps}
              disabled={isSubmitting}
              className="!h-12 !w-10 rounded-md border border-non-hover-border text-center font-studio text-lg text-black focus:border-black focus:outline-none"
            />
          )}
          containerStyle="flex gap-2"
        />
      </div>
      {error ? (
        <Body size="sm" className="mt-4 text-center text-red-600">
          {error}
        </Body>
      ) : null}
    </StudioPageFrame>
  );
};
